import type { JSX } from "react";
import { StyleSheet, Text, View } from "react-native";
import Svg, { Path, Rect } from "react-native-svg";

import { fontFamily } from "@passfirst/ui";

import { useColors, useTheme } from "./theme";

interface Props {
  /** Mark height in px; the wordmark scales with it. */
  size?: number;
  /** Hide the wordmark, render the mark alone (compact contexts). */
  markOnly?: boolean;
  /** Force white wordmark + white tile (for navy/immersive grounds). */
  onDark?: boolean;
}

// PassFirst lockup — navy tile + coral tick + "PassFirst" wordmark.
// Geometry matches the brand spec (mark M17 34 L28 45 L50 17 on a 64
// grid, tile rx 16/64). On dark grounds the tile flips to white, the
// tick stays coral, and the wordmark goes white. "Dark ground" is
// either an explicit navy panel (`onDark`) OR the app's dark theme —
// a navy tile on the near-black dark surface would otherwise vanish.
export function Lockup({
  size = 28,
  markOnly = false,
  onDark = false,
}: Props): JSX.Element {
  const colors = useColors();
  const { theme } = useTheme();
  const dark = onDark || theme === "dark";
  const tileFill = dark ? "#FFFFFF" : "#0F2A4A";
  const wordColor = onDark ? "#FFFFFF" : colors.heading;
  return (
    <View style={styles.row}>
      <Svg width={size} height={size} viewBox="0 0 64 64" aria-label="PassFirst">
        <Rect x={2} y={2} width={60} height={60} rx={16} fill={tileFill} />
        <Path
          d="M17 34 L28 45 L50 17"
          fill="none"
          stroke="#FF6B47"
          strokeWidth={6.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
      {markOnly ? null : (
        <Text
          style={[
            styles.wordmark,
            { fontSize: size * 0.72, color: wordColor },
          ]}
        >
          PassFirst
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: "row", alignItems: "center", gap: 9 },
  wordmark: {
    fontFamily: fontFamily.extraBold,
    letterSpacing: -0.5,
  },
});
